import { useApolloClient } from "@apollo/client";
import { getPathByRoute } from "App.js";
import routeKeys from "CustomerRouteKeys.js";
import React, { useContext } from "react";
import { useLocation } from 'react-router-dom';
import {
	levels,
	log,
	noticeError,
} from "utils/Logger.js";
import GoogleAnalytics from "utils/analytics/GoogleAnalytics.js";
import LogoutRequest from "./LogoutRequestContext.js";

// This Logout component is the one you'll want to reference if you're providing e.g. a logout link
// LogoutRequest is only the actual ajax call and doesn't handle clearing the Apollo cache.

export const LogoutContext = React.createContext(() => {
	log(null, levels.error, "LogoutContext used before it was ready");
});

const Logout = ({ children }) => {
	const logoutAjax = useContext(LogoutRequest);
	const client = useApolloClient();
	const { pathname } = useLocation();

	const logoutAndClearCache = async ({ redirectTo } = {}) => {
	// logout is a standard event
		// https://developers.google.com/analytics/devguides/collection/gtagjs/events#default_google_analytics_events
		GoogleAnalytics.logEvent('logout');

		// if logoutAjax fails, it automatically reloads the page
		await logoutAjax();

		// We're now logged out on the server-side.
		if (redirectTo) {
			// if redirectTo is provided, we should use it to redirect
			// we have to do a full page reload; otherwise SessionCheck's compareState will do it for us eventually
			window.location.href = redirectTo;
		}
		else if (pathname.match(/^\/account/)) {
			// don't want account urls added to postSigninGoToPath on logout
			// (url might be specific to a user and would error if a different user logged in)
			// there's no security benefit to this though because the history is still available with back button
			window.location.href = getPathByRoute(routeKeys.SignIn);
		}
		else if (pathname.match(/^\/guest/)) {
			window.location.href = "/register";
		}
		else {
			// Url isn't sensitive. E.g. user was signed in and on the homepage then clicked logout.

			// Clear the Apollo cache.
			// resetStore will rerun all queries. clearStore just clears cache for new queries
			// but doesn't rerender. resetStore will often error if there were requests in flight
			try {
				await client.resetStore();
			}
			catch (error) {
				noticeError(null, levels.info, error, "Error clearing Apollo store");
			}

			log(null, levels.info, "Apollo store cleared");
		}
	};
	return (
		<LogoutContext.Provider value={logoutAndClearCache}>
			{children}
		</LogoutContext.Provider>
	);
};

export const useLogoutContext = () => useContext(LogoutContext);

export default Logout;
